import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Backend, PERMISSION} from '@generated-backend-types';
import {DateTime} from 'luxon';
import {FormatInTimeZonePipe} from '../../../../../pipes/format-in-time-zone.pipe';
import {ParseIsoPipeModule} from 'ngx-date-fns';
import {de} from 'date-fns/locale';
import {FormatDurationPipe} from '../../../../../pipes/format-duration.pipe';
import {EditModeEnum} from '../../../../../enums/EditModeEnum';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {AutosizeModule} from 'ngx-autosize';
import {TIME_MODE} from '../../../../../components/form-inputs/date-time-selector/date-time-selector.component';
import {TimeTrackingEntryService} from '../../../../../services/api/time-tracking/time-tracking-entry.service';
import {FormSubmitService} from '../../../../../services/form-submit.service';
import {GridInlineFormGroupComponent} from './grid-inline-form-group/grid-inline-form-group.component';
import {UserSessionService} from '../../../../../services/user-session.service';
import {
  ConfirmDeleteModalComponent
} from '../../../../../components/confirm-delete-modal/confirm-delete-modal.component';
import {Subject, take, takeUntil} from 'rxjs';
import {BsModalService, ModalModule} from 'ngx-bootstrap/modal';
import {applyTimeToDate, normalizeTimeInput, TIME_PATTERN} from '../../../../../utils/time';
import PublicHoliday = Backend.App.Resources.Dashboard.TimeTracking.PublicHoliday;
import TimeTrackingEntry = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingEntry;
import TimeTrackingEntryTypeEnum = Backend.App.Enums.TimeTracking.TimeTrackingEntryTypeEnum;
import TimeTrackingEntryData = Backend.App.Data.Dashboard.TimeTracking.TimeTrackingEntryData;
import TimeTrackingOverviewEntry = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingOverview.TimeTrackingOverviewEntry;

@Component({
  selector: 'app-authenticated-time-tracking-overview-edit-date-row',
  templateUrl: './date-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslatePipe,
    FormatInTimeZonePipe,
    ParseIsoPipeModule,
    FormatDurationPipe,
    AutosizeModule,
    ReactiveFormsModule,
    GridInlineFormGroupComponent,
    ModalModule,
  ],
  providers: [BsModalService],
})
export class DateRowComponent implements OnInit, OnDestroy {
  @Input({required: true}) public date!: DateTime;
  @Input({required: true}) public entry?: TimeTrackingOverviewEntry;
  @Input({required: true}) public holiday?: PublicHoliday;
  @Input({required: true}) public canBeEdited = false;
  @Input({required: true}) public shouldWork = false;
  @Output() public entryChanged = new EventEmitter<void>();
  @Output() public entryDeleted = new EventEmitter<void>();
  public mode = EditModeEnum.SHOW;
  public form: StrictFormGroup<TimeTrackingEntryData> = this.fb.group({
    type: this.fb.control(TimeTrackingEntryTypeEnum.WORK, [Validators.required]),
    start_at: this.fb.control('', [Validators.required, Validators.pattern(TIME_PATTERN)]),
    end_at: this.fb.control('', [Validators.required, Validators.pattern(TIME_PATTERN)]),
    break_time: this.fb.control(0, [Validators.pattern(/^\d+$/)]),
    comment: this.fb.control(''),
  });
  protected onDestroy$: Subject<void> = new Subject<void>();
  protected readonly dfnsLocale = de;
  protected readonly EditModeEnum = EditModeEnum;
  protected readonly TimeTrackingEntryTypeEnum = TimeTrackingEntryTypeEnum;
  protected readonly TIME_MODE = TIME_MODE;
  protected readonly Object = Object;

  public constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly formSubmitService: FormSubmitService,
    private readonly timeTrackingEntryService: TimeTrackingEntryService,
    private readonly translate: TranslateService,
    private readonly userSessionService: UserSessionService,
    private readonly bsModalService: BsModalService,
  ) {
  }

  public get timeTrackingEntryTypes() {
    return Object.values(TimeTrackingEntryTypeEnum)
      .filter(type => ![
        TimeTrackingEntryTypeEnum.VACATION,
      ].includes(type));
  }

  /** Whether the given entry type requires start/end/break time inputs (WORK and EDUCATIONAL_LEAVE do). */
  public requiresTimeInput(type?: TimeTrackingEntryTypeEnum) {
    type = type ?? this.form.controls.type.value;
    return [TimeTrackingEntryTypeEnum.WORK, TimeTrackingEntryTypeEnum.EDUCATIONAL_LEAVE].includes(type);
  }

  protected readonly timeInputFields = [
    {control: 'start_at'},
    {control: 'end_at'},
    {control: 'break_time'},
  ] as const;

  public ngOnInit() {
    this.form.controls.start_at.valueChanges.subscribe(val => this.formatTime(val ?? '', this.form.controls.start_at));
    this.form.controls.end_at.valueChanges.subscribe(val => this.formatTime(val ?? '', this.form.controls.end_at));

    this.form.controls.type.valueChanges.subscribe(val => {
      if (!this.requiresTimeInput(val)) {
        this.form.controls.break_time.setValue(null);
        this.form.controls.break_time.markAsUntouched();
        this.form.controls.break_time.markAsPristine();
        this.form.controls.start_at.clearValidators();
        this.form.controls.end_at.clearValidators();
      } else {
        this.form.controls.start_at.setValidators([Validators.required, Validators.pattern(TIME_PATTERN)]);
        this.form.controls.end_at.setValidators([Validators.required, Validators.pattern(TIME_PATTERN)]);
      }
      this.form.controls.start_at.updateValueAndValidity();
      this.form.controls.end_at.updateValueAndValidity();
    });
  }

  public edit() {
    if (!this.canBeEdited) return;

    if (this.entry) {
      this.form.patchValue({
        type: this.entry.type,
        break_time: this.entry.break_time,
        start_at: this.entry.start_at ? DateTime.fromISO(this.entry.start_at).toFormat('HH:mm') : '',
        end_at: this.entry.end_at ? DateTime.fromISO(this.entry.end_at).toFormat('HH:mm') : '',
        comment: this.entry.comment,
      });
    } else {
      this.form.controls.type.setValue(TimeTrackingEntryTypeEnum.WORK);
      this.userSessionService.getStoredUserInfo().subscribe(user => {
        const timeTrackingEmployee = user?.time_tracking_employee;

        if (!timeTrackingEmployee) return;

        if (timeTrackingEmployee.default_time_tracking_entry_start_time) {
          this.setTime(this.form.controls.start_at, timeTrackingEmployee.default_time_tracking_entry_start_time);
          this.form.controls.start_at.markAsTouched();
        }

        if (timeTrackingEmployee.default_time_tracking_entry_end_time) {
          this.setTime(this.form.controls.end_at, timeTrackingEmployee.default_time_tracking_entry_end_time);
          this.form.controls.end_at.markAsTouched();
        }
      });
    }

    this.mode = this.entry ? EditModeEnum.EDIT : EditModeEnum.CREATE;
    this.cdr.markForCheck();
  }

  public save() {
    if (!this.canBeEdited || this.form.untouched) {
      this.mode = EditModeEnum.SHOW;
      return;
    }

    this.formSubmitService.submit<TimeTrackingEntry, TimeTrackingEntryData, TimeTrackingEntryService>({
      form: this.form,
      service: this.timeTrackingEntryService,
      modelId: this.entry?.time_tracking_entry_id ?? undefined,
      createPermission: PERMISSION.TIMETRACKING_TIMETRACKINGENTRY_CREATE_ALL,
      updatePermission: PERMISSION.TIMETRACKING_TIMETRACKINGENTRY_UPDATE_ALL,
      createSuccessMessage: this.translate.instant('authenticated.time_tracking.entries.create.success'),
      updateSuccessMessage: this.translate.instant('authenticated.time_tracking.entries.edit.success'),
      createErrorMessage: this.translate.instant('authenticated.time_tracking.entries.create.error'),
      updateErrorMessage: this.translate.instant('authenticated.time_tracking.entries.edit.error'),
      dataTransformer: (data): TimeTrackingEntryData => {
        let startAt: DateTime;
        let endAt: DateTime;

        if (this.requiresTimeInput()) {
          startAt = applyTimeToDate(this.date, data.start_at!);
          endAt = applyTimeToDate(this.date, data.end_at!);
        } else {
          startAt = this.date.set({hour: 12});
          endAt = this.date.set({hour: 12});
          data.break_time = null;
        }

        return {
          type: data.type,
          start_at: startAt.toJSDate().toISOString(),
          end_at: endAt.toJSDate().toISOString(),
          break_time: data.break_time,
          comment: data.comment,
        };
      }
    }).subscribe(entry => {
      if (!entry) return;
      this.mode = EditModeEnum.SHOW;
      this.entryChanged.emit();
      this.cdr.markForCheck();
    });
  }

  public delete() {
    if (!this.entry || !this.entry.time_tracking_entry_id) return;

    this.bsModalService.show(ConfirmDeleteModalComponent, {
      initialState: {
        modelId: this.entry.time_tracking_entry_id,
        dialogTranslation: this.translate.instant('authenticated.time_tracking.entries.delete.confirm_title'),
        successTranslation: this.translate.instant('authenticated.time_tracking.entries.delete.success'),
        errorTranslation: this.translate.instant('authenticated.time_tracking.entries.delete.error'),
        permissions: [PERMISSION.TIMETRACKING_TIMETRACKINGENTRY_DELETE_ALL],
        observable: this.timeTrackingEntryService.delete(this.entry.time_tracking_entry_id),
      }
    }).content?.deleteSuccess.pipe(
      takeUntil(this.onDestroy$),
      take(1)
    ).subscribe(() => {
      this.mode = EditModeEnum.SHOW;
      this.entryDeleted.emit();
      this.entry = undefined;
    });
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }

  private setTime(control: FormControl<string | null>, time: string): void {
    control.setValue(applyTimeToDate(this.date, time).toFormat('HH:mm'));
  }

  private formatTime(val: string, formControl: FormControl) {
    const normalized = normalizeTimeInput(val);

    if (normalized !== val) {
      formControl.setValue(normalized);
    }
  }
}
